import type { WallpaperConfig } from '../config/schema';
import { computeDotGrid, resolveSafeAreas } from '../lib/grid';
import { Dot } from '../components/Dot';
import {
  WEEKDAY_LETTERS_MON,
  WEEKDAY_LETTERS_SUN,
  isSameDay,
  monthGrid,
} from '../lib/dates';

interface Props {
  config: WallpaperConfig;
  today: Date;
  titleHeight: number;
}

export function MonthlyGrid({ config, today, titleHeight }: Props) {
  const { monthly, device, padding, colors, shape, highlightToday, showLabels } = config;
  const year = monthly.year ?? today.getFullYear();
  const month = monthly.month ?? today.getMonth() + 1;
  const weekStartsOn = monthly.weekStartDay === 'sunday' ? 0 : 1;
  const { cells, rows } = monthGrid(year, month, weekStartsOn);

  const headerReserved = showLabels ? 80 : 0;

  const { clockReserved, bottomReserved } = resolveSafeAreas(padding, device);

  const layout = computeDotGrid({
    width: device.width,
    height: device.height,
    paddingH: padding.horizontal,
    paddingV: padding.vertical,
    rows,
    cols: 7,
    topReserved: titleHeight + headerReserved + clockReserved,
    bottomReserved,
    // 7 cols × 5 rows on a tall portrait device would stretch pitchY ~1.55x
    // pitchX, leaving sparse vertical gaps. Cap at 1.3 so 5- and 6-row months
    // also render with similar per-row spacing.
    maxAspectRatio: 1.3,
  });

  const dots = cells.map((cell) => {
    const isPast = cell.date < today && !isSameDay(cell.date, today);
    const isToday = isSameDay(cell.date, today);
    let color: string;
    if (!cell.inMonth) {
      color = colors.empty;
    } else if (isToday && highlightToday) {
      color = colors.today;
    } else if (isPast) {
      color = colors.filled;
    } else {
      color = colors.empty;
    }
    const opacity = cell.inMonth ? 1 : 0.35;
    const { cx, cy } = layout.cellPosition(cell.weekIndex, cell.dayIndex);
    return (
      <g key={cell.date.toISOString()} opacity={opacity}>
        <Dot cx={cx} cy={cy} r={layout.dotRadius} color={color} shape={shape} />
      </g>
    );
  });

  const letters = weekStartsOn === 1 ? WEEKDAY_LETTERS_MON : WEEKDAY_LETTERS_SUN;
  const renderHeaders = () => {
    const fontSize = Math.min(layout.dotRadius * 0.9, headerReserved * 0.7);
    return letters.map((letter, idx) => {
      const { cx } = layout.cellPosition(0, idx);
      return (
        <text
          key={`${letter}-${idx}`}
          x={cx}
          y={layout.originY - layout.pitchY / 2 - fontSize * 0.4}
          fill={colors.text}
          fontFamily="ui-sans-serif, system-ui, -apple-system"
          fontSize={fontSize}
          textAnchor="middle"
          opacity={0.5}
        >
          {letter}
        </text>
      );
    });
  };
  const headers = showLabels ? renderHeaders() : null;

  return (
    <g data-mode="monthly">
      {headers}
      {dots}
    </g>
  );
}
